<template>
	<div
		:class="
			popup
				? 'px-4 pb-16 lg:pt-12 md:px-10 md:pb-10'
				: 'md:p-10 md:border md:border-border-tertiary md:shadow-sm md:rounded-[1.25rem] overflow-hidden'
		"
	>
		<div v-if="popup" class="flex items-center justify-between gap-4 max-lg:flex-wrap mb-4 md:mb-6 border-b border-border-tertiary pb-4 md:pb-6">
			<h2 class="typo-h3 text-heading-secondary">
				Výsledky vyhledávání <span v-if="getSearchValue.length">pro “{{ getSearchValue }}”</span>
			</h2>
			<InformationAboutHypermarket
				show-dropdown
				text="Informace o&nbsp;produktech se vztahují k&nbsp;pobočce"
				:default-hm="DEFAULT_HYPERMARKET"
				class="max-md:w-full"
			/>
		</div>

		<div v-if="getLoading">
			<NuxtIcon
				name="custom/loader"
				class="text-icon-primary p-2 text-2xl block [&_svg]:animate-spin [&_svg]:mx-auto"
				filled
			/>
		</div>
		<div v-else-if="!getSearchValue.length" class="text-center">
			<NuxtIcon name="search" filled class="text-[6.25rem] text-icon-primary [&_svg]:mx-auto mb-4 block" />
			<strong>
				Zadejte hledaný výraz nebo produkt který hledáte
			</strong>
		</div>
		<div v-else :class="{'overflow-y-auto max-h-[calc(75vh-5rem)] lg:max-h-[calc(80vh-8rem)]': popup}">
			<div v-if="!getProducts?.length" class="flex gap-2 sm:gap-6 sm:items-center mb-4 md:mb-10 pb-4 md:pb-10 border-b border-border-tertiary">
				<NuxtIcon name="frown" filled class="text-4xl sm:text-[6.25rem] text-icon-primary" aria-hidden="true" />
				<p class="typo-body-m">
					Bohužel, žádné produkty jsme pro tento výraz nenašli.<br aria-hidden="true" />
					Ale určitě se Vám budou líbit další produkty z naší nabídky.
				</p>
			</div>
			<template v-else>
				<div v-if="!hideFirstHeading" class="py-4 max-lg:border-t-2 lg:border-b-2 border-border-tertiary typo-body-s lg:mb-4 max-lg:hidden">
					Produkty
				</div>
				<SearchProductList :products="getProducts" class="mb-4 md:mb-8" />
				<div class="text-center md:py-6 mb-4 md:mb-10">
					<button
						type="button"
						class="btn btn-primary btn-lg justify-center max-md:w-full"
						:disabled="!getProductsPaginationShowMore || getProductsLoadingMore"
						@click="loadMoreProducts"
					>
						<template v-if="getProductsLoadingMore">
							Nahrávám
						</template>
						<template v-else>
							Zobrazit další výsledky
							<NuxtIcon name="custom/arrow-right" filled class="text-xl md:hidden" />
						</template>
					</button>
				</div>

				<!-- TODO: Docasne schovavame kvuli spatnym datum z API -->
				<!-- <SearchBox v-if="getOftenProducts?.length" title="Mohlo by se vám líbit">
					<template #default>
						<SearchProductList :products="getOftenProducts" class="mb-8" />
					</template>
					<template #cta>
						<AbsoluteInternalLink :to="branchSpecificPath({name: 'hypermarket-akcni-nabidka-category'})" class="btn btn-primary btn-lg">
							Akční nabídka
						</AbsoluteInternalLink>
					</template>
				</SearchBox> -->
			</template>

			<SearchResultsBox v-if="getRecipes?.length">
				<template #default>
					<div class="py-4 max-lg:border-t-2 lg:border-b-2 border-border-tertiary typo-body-s lg:mb-4">
						Recepty
					</div>
					<SearchArticleList :items="getRecipes" />
				</template>
				<template #cta>
					<AbsoluteInternalLink
						:to="{name: 'recepty'}"
						class="btn btn-primary btn-lg justify-center max-md:w-full"
						@click="setVisibleSearchPopup(false)"
					>
						Zobrazit  všechny
						<NuxtIcon name="custom/arrow-right" filled class="text-xl md:hidden" />
					</AbsoluteInternalLink>
				</template>
			</SearchResultsBox>

			<SearchResultsBox v-if="getBlogs?.length">
				<template #default>
					<div class="py-4 max-lg:border-t-2 lg:border-b-2 border-border-tertiary typo-body-s lg:mb-4">
						Blog
					</div>
					<SearchArticleList :items="getBlogs" />
				</template>
				<template #cta>
					<AbsoluteInternalLink
						:to="{name: 'blog'}"
						class="btn btn-primary btn-lg justify-center max-md:w-full"
						@click="setVisibleSearchPopup(false)"
					>
						Zobrazit  všechny
						<NuxtIcon name="custom/arrow-right" filled class="text-xl md:hidden" />
					</AbsoluteInternalLink>
				</template>
			</SearchResultsBox>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useSearchStore} from '~/stores/search';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {DEFAULT_HYPERMARKET} from '#shared/constants';

	type Props = {
		popup?: boolean;
		hideFirstHeading?: boolean;
	};

	const props = defineProps<Props>();

	const searchStore = useSearchStore();
	const {
		visibleSearchPopup,
		getSearchValue,
		getLoading,
		getProducts,
		getProductsPaginationShowMore,
		getProductsLoadingMore,
		// getOftenProducts, // TODO: Docasne schovavame kvuli spatnym datum z API
		getBlogs,
		getRecipes,
	} = storeToRefs(searchStore);

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);

	const route = useRoute();
	const searchResultsRoute = computed(() => ['branch-vysledky-vyhledavani', 'vysledky-vyhledavani'].includes(String(route.name)));
	let componentMounted = false;
	let analyticsViewKey: string | null = null;
	let analyticsInitialResultsTracked = false;
	let analyticsTrackedProductsCount = 0;

	onMounted(() => {
		componentMounted = true;
		trackVisibleSearchResults();
	});

	onBeforeUnmount(() => {
		componentMounted = false;
	});

	watch(
		[
			getLoading,
			getSearchValue,
			getProducts,
			visibleSearchPopup,
			() => route.fullPath,
			selectedHypermarketWithFallbackInstance,
		],
		() => {
			trackVisibleSearchResults();
		},
		{flush: 'post'},
	);

	watch(visibleSearchPopup, (visible) => {
		if (props.popup && !visible) {
			resetAnalyticsTracking();
		}
	});

	function loadMoreProducts() {
		searchStore.loadMoreProducts(Number(selectedHypermarketWithFallbackInstance.value?.content.gsoaId));
	}

	function setVisibleSearchPopup(state: boolean) {
		searchStore.setVisibleSearchPopup(state);
	}

	async function trackVisibleSearchResults() {
		if (
			!componentMounted
			|| getLoading.value
			|| getSearchValue.value.length < 3
			|| (props.popup ? !visibleSearchPopup.value : !searchResultsRoute.value)
		) {
			return;
		}

		const query = getSearchValue.value;
		const gsoaId = Number(selectedHypermarketWithFallbackInstance.value?.content.gsoaId);
		if (!gsoaId || !searchStore.hasSearchData(query, gsoaId)) {
			return;
		}

		await nextTick();

		if (
			!componentMounted
			|| getLoading.value
			|| getSearchValue.value !== query
			|| Number(selectedHypermarketWithFallbackInstance.value?.content.gsoaId) !== gsoaId
			|| (props.popup ? !visibleSearchPopup.value : !searchResultsRoute.value)
			|| !searchStore.hasSearchData(query, gsoaId)
		) {
			return;
		}

		const viewKey = `${props.popup ? 'popup' : 'page'}:${route.fullPath}:${gsoaId}:${query}`;
		if (analyticsViewKey !== viewKey) {
			resetAnalyticsTracking();
			analyticsViewKey = viewKey;
		}

		const visibleProducts = getProducts.value;
		if (visibleProducts.length === 0) {
			if (analyticsInitialResultsTracked) {
				return;
			}

			analyticsInitialResultsTracked = true;
			searchStore.trackSearchAnalytics({text: query, products: []});
			return;
		}

		const positionOffset = analyticsInitialResultsTracked ? analyticsTrackedProductsCount : 0;
		if (visibleProducts.length <= positionOffset) {
			return;
		}

		const newProducts = visibleProducts.slice(positionOffset);
		analyticsInitialResultsTracked = true;
		analyticsTrackedProductsCount = visibleProducts.length;
		searchStore.trackSearchAnalytics({
			text: query,
			products: newProducts,
			positionOffset,
		});
	}

	function resetAnalyticsTracking() {
		analyticsViewKey = null;
		analyticsInitialResultsTracked = false;
		analyticsTrackedProductsCount = 0;
	}
</script>
